/**
 * Concept Graph Explorer Page
 *
 * D3-based hierarchical graph visualization showing progressive concept relationships.
 * Horizontal layout: Prerequisites (left) → Advanced concepts (right)
 *
 * @module pages/ConceptGraphExplorer
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ConceptCard, fetchConcepts, fetchConcept } from '../features/concept-graph';
import { ConceptGraphVisualization } from '../features/concept-graph/components/ConceptGraphVisualization';
import { ConceptComparisonView } from '../features/concept-graph/components/ConceptComparisonView';
import { FloatingMenu, type ViewMode } from '../features/concept-graph/components/FloatingMenu';
import { TwoTierCategoryNav, type PrimaryCategory } from '../features/concept-graph/components/TwoTierCategoryNav';
import type { Concept, ConceptCategory } from '../features/concept-graph';
import { Network } from 'lucide-react';

export function ConceptGraphExplorer() {
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [filteredConcepts, setFilteredConcepts] = useState<Concept[]>([]);
  const [selectedConcept, setSelectedConcept] = useState<Concept | null>(null);
  const [primaryCategory, setPrimaryCategory] = useState<PrimaryCategory>('overview');
  const [secondaryCategory, setSecondaryCategory] = useState<ConceptCategory | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('comparison'); // Default to comparison (80% use case)
  const [pathMode, setPathMode] = useState(false);
  const [localMode, setLocalMode] = useState(false); // Show only 1-hop neighborhood
  const [selectedComplexities, setSelectedComplexities] = useState<number[]>([1, 2, 3, 4, 5]); // All complexities by default
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load all concepts on mount
  useEffect(() => {
    loadConcepts();
  }, []);

  // Category mapping for two-tier system
  const PRIMARY_TO_CATEGORIES: Record<PrimaryCategory, ConceptCategory[]> = {
    overview: [], // All categories
    core: ['memory_management', 'types', 'control_flow', 'error_handling'],
    advanced: ['concurrency', 'io', 'modules', 'metaprogramming'],
    patterns: ['design_patterns', 'paradigms', 'data_structures', 'functions']
  };

  // Filter concepts when category, search, or complexity changes
  useEffect(() => {
    let filtered = concepts;

    // Filter by primary/secondary category
    if (primaryCategory !== 'overview') {
      if (secondaryCategory) {
        // Secondary selected: show only that category
        filtered = filtered.filter(c => c.category === secondaryCategory);
      } else {
        // Primary selected, no secondary: show all categories in that primary
        const allowedCategories = PRIMARY_TO_CATEGORIES[primaryCategory];
        filtered = filtered.filter(c => allowedCategories.includes(c.category));
      }
    }
    // If overview: show all concepts (no filtering)

    // Filter by complexity
    if (selectedComplexities.length < 5) {
      filtered = filtered.filter(c => selectedComplexities.includes(c.difficulty));
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query) ||
        c.category.toLowerCase().includes(query)
      );
    }

    setFilteredConcepts(filtered);
  }, [concepts, primaryCategory, secondaryCategory, searchQuery, selectedComplexities]);

  const loadConcepts = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await fetchConcepts({ limit: 100 });
      setConcepts(result.concepts);
      setFilteredConcepts(result.concepts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load concepts');
      console.error('Error loading concepts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConceptClick = async (conceptId: string) => {
    try {
      const concept = await fetchConcept(conceptId);
      setSelectedConcept(concept);
    } catch (err) {
      console.error('Error loading concept:', err);
    }
  };

  const togglePathMode = () => {
    setPathMode(!pathMode);
  };

  const toggleLocalMode = () => {
    setLocalMode(!localMode);
  };

  const handlePrimaryChange = (primary: PrimaryCategory, secondary: ConceptCategory | null) => {
    setPrimaryCategory(primary);
    setSecondaryCategory(secondary);
  };

  const handleSecondaryChange = (secondary: ConceptCategory | null) => {
    setSecondaryCategory(secondary);
  };

  const handleViewGraph = async (conceptId: string) => {
    // Select the concept, switch to graph view, and enable local mode
    // Per spec: clicking "View Graph" should show local neighborhood
    try {
      const concept = await fetchConcept(conceptId);
      setSelectedConcept(concept);
      setViewMode('graph');
      setLocalMode(true); // Enable local mode to show 1-hop neighborhood
    } catch (err) {
      console.error('Error loading concept:', err);
    }
  };

  // Category options
  const categories: Array<{ value: ConceptCategory | 'all'; label: string }> = [
    { value: 'all', label: 'All Categories' },
    { value: 'memory_management', label: 'Memory Management' },
    { value: 'control_flow', label: 'Control Flow' },
    { value: 'data_structures', label: 'Data Structures' },
    { value: 'functions', label: 'Functions' },
    { value: 'types', label: 'Types' },
    { value: 'concurrency', label: 'Concurrency' },
    { value: 'io', label: 'I/O' },
    { value: 'error_handling', label: 'Error Handling' },
    { value: 'modules', label: 'Modules' },
    { value: 'metaprogramming', label: 'Metaprogramming' },
    { value: 'design_patterns', label: 'Design Patterns' },
    { value: 'paradigms', label: 'Paradigms' }
  ];

  // Calculate concept counts for each category
  const conceptCounts = useMemo(() => {
    const counts: Record<string, number> = { all: concepts.length };

    concepts.forEach(concept => {
      const category = concept.category;
      counts[category] = (counts[category] || 0) + 1;
    });

    return counts as Record<ConceptCategory | 'all', number>;
  }, [concepts]);

  return (
    <div className="h-screen flex flex-col bg-warm-bg relative">
      {/* Two-Tier Category Navigation - Linear Style */}
      <TwoTierCategoryNav
        selectedPrimary={primaryCategory}
        selectedSecondary={secondaryCategory}
        onPrimaryChange={handlePrimaryChange}
        onSecondaryChange={handleSecondaryChange}
        conceptCounts={conceptCounts}
      />

      {/* Floating Menu - Invisible UI */}
      <FloatingMenu
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategory={secondaryCategory || 'all'}
        onCategoryChange={(cat) => setSecondaryCategory(cat === 'all' ? null : cat as ConceptCategory)}
        pathMode={pathMode}
        onPathModeToggle={togglePathMode}
        localMode={localMode}
        onLocalModeToggle={toggleLocalMode}
        selectedComplexities={selectedComplexities}
        onComplexityChange={setSelectedComplexities}
        conceptCount={filteredConcepts.length}
        categories={categories}
      />

      {/* Main Content Area - Fullscreen Two Pane Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Pane: View Content (Comparison or Graph) */}
        <div className="flex-1 overflow-hidden">
          {/* Loading State */}
          {isLoading && (
            <div className="h-full flex items-center justify-center bg-white rounded-lg border border-slate-200 m-4">
              <div className="text-center">
                <div className="inline-block w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-slate-600">Loading concepts...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="h-full flex items-center justify-center bg-white rounded-lg border border-red-200 m-4">
              <div className="text-center p-8">
                <p className="text-red-700 mb-2">Error loading concepts</p>
                <p className="text-sm text-red-600">{error}</p>
                <button
                  onClick={loadConcepts}
                  className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Comparison View */}
          {!isLoading && !error && filteredConcepts.length > 0 && viewMode === 'comparison' && (
            <ConceptComparisonView
              concepts={filteredConcepts}
              selectedConceptId={selectedConcept?.id}
              onConceptClick={handleConceptClick}
              onViewGraph={handleViewGraph}
              availableLanguages={['C', 'Rust', 'Go']}
              visibleLanguages={['C', 'Rust', 'Go']}
            />
          )}

          {/* Graph View */}
          {!isLoading && !error && filteredConcepts.length > 0 && viewMode === 'graph' && (
            <div className="h-full p-4">
              <ConceptGraphVisualization
                concepts={filteredConcepts}
                onConceptClick={handleConceptClick}
                selectedConceptId={selectedConcept?.id}
                pathMode={pathMode}
                localMode={localMode}
              />
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && filteredConcepts.length === 0 && (
            <div className="h-full flex items-center justify-center bg-white rounded-lg border border-slate-200 m-4">
              <div className="text-center">
                <Network className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600 mb-1">No concepts found</p>
                <p className="text-sm text-slate-400">Try adjusting your search or filters</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Pane: Detail Panel (Always Visible, Fixed Width) */}
        <div className="w-[500px] border-l border-slate-200 bg-white flex flex-col overflow-hidden shadow-xl">
          {/* Panel Header */}
          <div className="flex-shrink-0 p-4 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-white">
            <h2 className="text-lg font-semibold text-slate-900">Concept Details</h2>
            {selectedConcept && (
              <p className="text-xs text-slate-600 mt-1">
                Click concepts in the graph to explore
              </p>
            )}
          </div>

          {/* Panel Content (Scrollable) */}
          <div className="flex-1 overflow-y-auto">
            {selectedConcept ? (
              <div className="p-4">
                <ConceptCard
                  concept={selectedConcept}
                  showRelationships={true}
                  onRelatedClick={handleConceptClick}
                />
              </div>
            ) : (
              <div className="h-full flex items-center justify-center p-8">
                <div className="text-center">
                  <Network className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-600 font-medium mb-2">No concept selected</p>
                  <p className="text-sm text-slate-400 max-w-xs mx-auto">
                    Click on any concept node in the graph to view detailed information, code examples, and relationships
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

export default ConceptGraphExplorer;
